import { MetricCard, DataTable, KeyValueList, ExpandableList } from '../primitives'

interface SmartRendererProps {
  data: unknown
}

// Keys that typically contain metrics/scores
const METRIC_PATTERNS = [
  /score/i,
  /total/i,
  /count/i,
  /time/i,
  /duration/i,
  /amount/i,
  /quantity/i,
  /size/i,
  /length/i,
  /limit/i,
]

// Keys to exclude from display
const EXCLUDED_KEYS = ['_hint', 'success', 'status_code']

function isMetricKey(key: string): boolean {
  return METRIC_PATTERNS.some((pattern) => pattern.test(key))
}

function getUnit(key: string): string | undefined {
  if (/time.*ms/i.test(key) || key.endsWith('_ms')) return 'ms'
  if (/time.*s/i.test(key) || key.endsWith('_seconds')) return 's'
  if (/time/i.test(key)) return ''
  return undefined
}

export function SmartRenderer({ data }: SmartRendererProps) {
  // Handle null/undefined
  if (data === null || data === undefined) {
    return <div className="text-sm text-muted-foreground italic">No data returned</div>
  }

  // Handle primitive types
  if (typeof data === 'string') {
    return <pre className="text-xs whitespace-pre-wrap">{data}</pre>
  }

  if (typeof data === 'number') {
    return <MetricCard label="Result" value={data} size="md" variant="success" />
  }

  if (typeof data === 'boolean') {
    return (
      <MetricCard
        label="Result"
        value={data ? 'Yes' : 'No'}
        size="md"
        variant={data ? 'success' : 'warning'}
      />
    )
  }

  // Handle arrays
  if (Array.isArray(data)) {
    if (data.length === 0) {
      return <div className="text-sm text-muted-foreground italic">Empty result</div>
    }

    // If array of objects, render as table
    if (typeof data[0] === 'object' && data[0] !== null) {
      return <DataTable data={data as Record<string, unknown>[]} />
    }

    // If array of primitives, render as expandable list
    return <ExpandableList items={data} initialVisible={10} />
  }

  // Handle objects
  if (typeof data === 'object') {
    const obj = data as Record<string, unknown>
    const keys = Object.keys(obj).filter((k) => !EXCLUDED_KEYS.includes(k) && !k.startsWith('_'))

    // Separate into categories
    const metricKeys: string[] = []
    const arrayKeys: string[] = []
    const objectKeys: string[] = []
    const simpleKeys: string[] = []

    keys.forEach((key) => {
      const value = obj[key]
      if (value === null || value === undefined) return

      if (Array.isArray(value) && value.length > 0) {
        arrayKeys.push(key)
      } else if (typeof value === 'object') {
        objectKeys.push(key)
      } else if (typeof value === 'number' && isMetricKey(key)) {
        metricKeys.push(key)
      } else {
        simpleKeys.push(key)
      }
    })

    return (
      <div className="space-y-3">
        {/* Metrics row */}
        {metricKeys.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {metricKeys.slice(0, 6).map((key) => (
              <MetricCard
                key={key}
                label={key}
                value={obj[key] as number}
                unit={getUnit(key)}
                size="sm"
              />
            ))}
          </div>
        )}

        {/* Arrays as tables */}
        {arrayKeys.map((key) => {
          const arr = obj[key] as unknown[]
          if (arr.length > 0 && typeof arr[0] === 'object' && arr[0] !== null) {
            return (
              <DataTable key={key} title={key} data={arr as Record<string, unknown>[]} compact />
            )
          }
          return <ExpandableList key={key} items={arr} title={key} initialVisible={5} compact />
        })}

        {/* Simple key-values */}
        {simpleKeys.length > 0 && (
          <KeyValueList
            data={Object.fromEntries(simpleKeys.map((k) => [k, obj[k]]))}
            excludeKeys={metricKeys}
            compact
          />
        )}

        {/* Nested objects (rendered recursively) */}
        {objectKeys.map((key) => (
          <div key={key} className="space-y-1">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              {key}
            </h4>
            <div className="ml-2 pl-2 border-l-2 border-muted">
              <SmartRenderer data={obj[key]} />
            </div>
          </div>
        ))}
      </div>
    )
  }

  // Fallback: stringify
  return (
    <pre className="text-[11px] font-mono bg-muted/30 p-2 rounded overflow-x-auto whitespace-pre-wrap">
      {JSON.stringify(data, null, 2)}
    </pre>
  )
}
